import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';

const drawerWidth = 280;

export default function AppLayoutMobile({ user, menuItems, onLogout, children }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const handleOpen = () => setDrawerOpen(true);
  const handleClose = () => setDrawerOpen(false);
  const handleNavigate = (path) => { navigate(path); handleClose(); };
  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{ display: 'flex', alignItems: 'center', px: 2, minHeight: 56 }}>
        <img src="/logo.svg" alt="SofiaPulse" style={{ height: 26 }} />
      </Toolbar>
      <Divider />
      <List sx={{ px: 2, py: 1, flexGrow: 1 }}>
        {menuItems.map((item) => {
          if (item.requiredRole && user?.claims?.roles) {
            const has =
              user.claims.roles.includes(item.requiredRole) ||
              user.claims.roles.includes('system_admin') ||
              user.claims.roles.includes('company_manager');
            if (!has) return null;
          }
          if (item.systemOnly && user?.claims?.roles && !user.claims.roles.includes('system_admin')) {
            return null;
          }
          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => { if (!item.disabled) handleNavigate(item.path); }}
                selected={isActive}
                disabled={item.disabled}
                sx={{
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': { backgroundColor: 'primary.dark' },
                    '& .MuiListItemIcon-root': { color: 'white' },
                  },
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      <Divider />
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
          Logged in as
        </Typography>
        <Typography variant="body2" fontWeight={500}>
          {user?.email}
        </Typography>
        <Chip label={user?.claims?.roles?.[0] || 'Unknown'} size="small" color="primary" sx={{ mt: 1 }} />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: 'background.default' }}>
      <AppBar position="fixed" sx={{ width: '100%' }}>
        <Toolbar sx={{ minHeight: 56 }}>
          <IconButton color="inherit" onClick={handleOpen} edge="start" sx={{ mr: 1.5 }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontSize: '1rem' }}>
            {menuItems.find((item) => item.path === location.pathname)?.text ?? 'Admin Dashboard'}
          </Typography>
          <Tooltip title="Account">
            <IconButton
              onClick={handleMenuOpen}
              size="small"
              sx={{ ml: 1 }}
              aria-controls={anchorEl ? 'account-menu-mobile' : undefined}
              aria-haspopup="true"
              aria-expanded={anchorEl ? 'true' : undefined}
            >
              <Avatar
                src={`https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(user?.displayName || user?.email || '')}&backgroundColor=7c3aed&textColor=ffffff`}
                alt={user?.displayName || user?.email}
                sx={{ width: 30, height: 30 }}
              >
                {user?.email?.[0]?.toUpperCase()}
              </Avatar>
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={anchorEl}
        id="account-menu-mobile"
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={onLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      <Drawer
        variant="temporary"
        anchor="left"
        open={drawerOpen}
        onClose={handleClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: 'none',
            boxShadow: '2px 0 12px rgba(97,60,146,0.15)',
            backgroundColor: '#faf5ff',
          },
        }}
      >
        {drawer}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          px: 2,
          pb: 3,
          width: '100%',
          boxSizing: 'border-box',
          overflowX: 'hidden',
        }}
      >
        <Toolbar sx={{ minHeight: 56 }} />
        {children}
      </Box>
    </Box>
  );
}
